import { computed, Injectable } from '@angular/core';
import { Automation, WorkflowStepInterface } from '@vendasta/automata';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { AutomationViewService } from '../automation/automation-view.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { createStepMap, createWorkflowGraph } from './automation-editor.utility';
import { StepNameService } from './services/step-name.service';
import { CurrentAutomationService } from '@galaxy/automata/shared';

export type SideMenuTypes =
  | 'selectNewStep'
  | 'addStep'
  | 'editStep'
  | 'editEnd'
  | 'selectNewTrigger'
  | 'addTrigger'
  | 'editTrigger'
  | 'addGoal'
  | 'editGoal'
  | '';

@Injectable({ providedIn: 'root' })
export class AutomationEditorService {
  private automation$: Observable<Automation | null> = this.currentAutomationService.automation$;

  public stepMap = computed(() => createStepMap(this.currentAutomationService.automation()));
  // A graph representation of the automation for our graphing library
  public graphMap = computed(() => {
    return createWorkflowGraph(
      this.stepMap(),
      this.currentAutomationService.automation(),
      this.automationViewService.expandedGroups(),
    );
  });

  constructor(
    private readonly automationViewService: AutomationViewService,
    private readonly currentAutomationService: CurrentAutomationService,
    private readonly stepNameService: StepNameService,
  ) {}

  public expandGroup(groupId: string): void {
    this.automationViewService.expandGroup(groupId);
  }

  public collapseGroup(groupId: string): void {
    this.automationViewService.collapseGroup(groupId);
  }

  automationStepMap$: Observable<Map<string, WorkflowStepInterface>> = this.automation$.pipe(
    map((automation) => {
      const stepMap = new Map<string, WorkflowStepInterface>();
      if (automation?.triggerStep) {
        stepMap.set(automation.triggerStep.id, automation.triggerStep);
      }
      if (automation?.triggerFilters) {
        automation.triggerFilters.forEach((filterStep) => stepMap.set(filterStep.id, filterStep));
      }
      if (automation?.workflow) {
        automation.workflow.forEach((workflowStep) => stepMap.set(workflowStep.id, workflowStep));
      }
      return stepMap;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  automationStepMap = toSignal(this.automationStepMap$);

  // Results in `name of step (num matching step names)`
  public buildBestAttemptUniqueName(name: string): string {
    return this.stepNameService.buildBestAttemptUniqueName(name, this.currentAutomationService.getCurrentAutomation());
  }
}
